import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useTenderDetails } from '../../contexts/TenderDetailsContext';
import { searchKnowledgeBase } from '../../services/api';
import type { RecommendedTeamMember } from '../../types/tender';
import { type Column, StandardTable } from '../common/UI/StandardTable';
import WorkflowStep from './WorkflowStep';

const EMPTY_ARRAY: never[] = [];

const ROLE_SUGGESTIONS = [
  'Project Director',
  'Project Manager',
  'Contract Administrator',
  'Site Manager',
  'HSEQ Manager',
  'Specialist',
  'Lead Engineer',
  'Estimator',
  'Commercial Manager',
  'Procurement Manager',
  'Technical Lead',
  'Graduate',
  'Intern',
];

const SPECIALIZATION_SUGGESTIONS = [
  'Engineering',
  'Design',
  'Management',
  'Operations',
  'Commercial',
  'Safety',
  'Quality',
  'Technical Delivery',
  'Software Development',
  'Project Controls',
  'Planning',
  'Stakeholder Management',
  'Environmental',
  'Sustainability',
  'Risk Management',
];

// Helper to normalize names for basic matching
const normalizeName = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

const ManageTeamStep: React.FC = () => {
  const theme = useTheme();
  const {
    canManageTender,
    openStep,
    setOpenStep,
    tender,
    handleUpdateTender,
    users,
    user_docs,
    handleCVDelete,
    handleDeleteSupportDoc,
  } = useTenderDetails();
  const { account } = useAccount();
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [liveSearchResults, setLiveSearchResults] = useState<RecommendedTeamMember[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [addingMemberIds, setAddingMemberIds] = useState<string[]>([]);

  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      setLiveSearchResults([]);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(async () => {
      try {
        if (!account?.clientId) {
          return;
        }
        const response = await searchKnowledgeBase({
          query: searchQuery,
          clientId: account.clientId,
          folderPaths: ['people_cv', 'team_cvs'],
          maxResults: 10,
        });

        if (response.data?.success && response.data.results) {
          const formattedResults: RecommendedTeamMember[] = response.data.results.map(
            (res: Record<string, unknown>) => ({
              memberId: (res.fileName as string)?.split('.')[0] || (res.path as string),
              displayName:
                (res.fileName as string)
                  ?.replace('.pdf', '')
                  .replace('.docx', '')
                  .replace('.md', '') || 'Unknown',
              role: 'Search Result',
              matchScore: (res.rating as number) || 0,
              reason: (res.matchReason as string) || 'Found via live search',
              skills: [],
              specialization: 'Manual Search',
              region: 'Unknown',
              includeInDraft: false,
              cvFileUrl: res.path as string,
            })
          );
          setLiveSearchResults(formattedResults);
        }
      } catch (_err) {
        // Silently fail search if backend errors
      } finally {
        setIsSearching(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, account?.clientId]);

  if (!canManageTender) {
    return null;
  }

  const activeMembers = tender.teamMembers || EMPTY_ARRAY;

  // Combine AI recommendations with Live Search results, deduplicating by name
  const combinedSuggestions = [
    ...(tender.alignmentAnalysis?.recommendedTeamMembers || []),
    ...liveSearchResults,
  ].filter((v, i, a) => a.findIndex((t) => t.displayName === v.displayName) === i);

  // Find suggestions that aren't already in the active members list and match search
  const suggestedMembers = combinedSuggestions
    .filter((suggested) => {
      if (activeMembers.some((active) => active.displayName === suggested.displayName)) {
        return false;
      }
      if (searchQuery && liveSearchResults.length === 0) {
        const query = searchQuery.toLowerCase();
        return (
          suggested.displayName.toLowerCase().includes(query) ||
          suggested.specialization?.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));

  const handleRoleChange = (memberId: string, role: string) => {
    const updatedMembers = [...activeMembers];
    const index = updatedMembers.findIndex((m) => (m.userId || m.displayName) === memberId);
    if (index === -1) {
      return;
    }
    const memberToUpdate = { ...updatedMembers[index] } as RecommendedTeamMember;

    memberToUpdate.role = role || '';
    updatedMembers[index] = memberToUpdate;
    handleUpdateTender('teamMembers', updatedMembers);
  };

  const handleSpecializationChange = (memberId: string, specialization: string) => {
    const updatedMembers = [...activeMembers];
    const index = updatedMembers.findIndex((m) => (m.userId || m.displayName) === memberId);
    if (index === -1) {
      return;
    }
    const memberToUpdate = { ...updatedMembers[index] } as RecommendedTeamMember;

    memberToUpdate.specialization = specialization || '';
    updatedMembers[index] = memberToUpdate;
    handleUpdateTender('teamMembers', updatedMembers);
  };

  const handleRemoveMember = async (memberId: string) => {
    const memberToRemove = activeMembers.find((m) => (m.userId || m.displayName) === memberId);
    const updatedMembers = activeMembers.filter((m) => (m.userId || m.displayName) !== memberId);

    await handleUpdateTender('teamMembers', updatedMembers);

    if (memberToRemove?.displayName) {
      const cvDoc = user_docs.find(
        (d) =>
          d.originalFilename === memberToRemove.displayName &&
          (d.doc_type === 'cv' || d.doc_type === 'people_cv')
      );
      if (cvDoc) {
        await handleCVDelete(cvDoc, handleDeleteSupportDoc);
      }
    }
  };

  const handleAddSuggestion = async (member: RecommendedTeamMember) => {
    const memberId = member.userId || member.displayName;
    setAddingMemberIds((prev) => [...prev, memberId]);
    try {
      const updatedMembers = [...activeMembers, { ...member, includeInDraft: true }];
      await handleUpdateTender('teamMembers', updatedMembers);
      // Don't auto close modal, allow them to add multiple
    } finally {
      setAddingMemberIds((prev) => prev.filter((id) => id !== memberId));
    }
  };

  const activeColumns: Column<RecommendedTeamMember>[] = [
    {
      id: 'name',
      label: 'Name',
      width: '20%',
      render: (member) => {
        // Check if there is a matching user in the platform
        const normalizedMemberName = normalizeName(member.displayName);
        const matchingUser = users?.find(
          (u) => normalizeName(u.displayName || '') === normalizedMemberName
        );

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {member.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {member.region}
              </Typography>
            </Box>
            {matchingUser && (
              <Tooltip title="Matched to a registered platform user">
                <CheckCircleIcon color="success" sx={{ fontSize: 16, ml: 0.5 }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      id: 'role',
      label: 'Role',
      width: '30%',
      render: (member) => {
        const memberId = member.userId || member.displayName;
        return (
          <Autocomplete
            freeSolo
            fullWidth
            options={ROLE_SUGGESTIONS}
            value={member.role || ''}
            onChange={(_, newValue) => handleRoleChange(memberId, newValue || '')}
            onInputChange={(_, newInputValue) => handleRoleChange(memberId, newInputValue)}
            renderInput={(params) => (
              <TextField {...params} size="small" placeholder="Enter role..." />
            )}
          />
        );
      },
    },
    {
      id: 'specialization',
      label: 'Specialization',
      width: '40%',
      render: (member) => {
        const memberId = member.userId || member.displayName;
        return (
          <Autocomplete
            freeSolo
            fullWidth
            options={SPECIALIZATION_SUGGESTIONS}
            value={member.specialization || ''}
            onChange={(_, newValue) => handleSpecializationChange(memberId, newValue || '')}
            onInputChange={(_, newInputValue) =>
              handleSpecializationChange(memberId, newInputValue)
            }
            renderInput={(params) => (
              <TextField {...params} size="small" placeholder="Specialization..." />
            )}
          />
        );
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      width: '10%',
      render: (member) => {
        const memberId = member.userId || member.displayName;
        return (
          <IconButton size="small" color="error" onClick={() => handleRemoveMember(memberId)}>
            <DeleteIcon sx={{ fontSize: 28 }} />
          </IconButton>
        );
      },
    },
  ];

  const suggestedColumns: Column<RecommendedTeamMember>[] = [
    {
      id: 'name',
      label: 'Name',
      render: (member) => (
        <>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {member.displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {member.region}
          </Typography>
        </>
      ),
    },
    {
      id: 'specialization',
      label: 'Specialization',
      render: (member) => member.specialization,
    },
    {
      id: 'score',
      label: 'Match Score',
      render: (member) => `${member.matchScore}%`,
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (member) => {
        const memberId = member.userId || member.displayName;
        const isAdding = addingMemberIds.includes(memberId);
        return (
          <Button
            variant="contained"
            size="small"
            startIcon={isAdding ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            disabled={isAdding}
            onClick={() => handleAddSuggestion(member)}
          >
            {isAdding ? 'Adding...' : 'Add to Team'}
          </Button>
        );
      },
    },
  ];

  return (
    <WorkflowStep
      stepNumber={4}
      expanded={openStep === 4}
      onChange={(_, isExpanded) => setOpenStep(isExpanded ? 4 : null)}
      title="Manage Team"
      description="Build a rank-ordered team for this tender using match scoring."
      isComplete={activeMembers.length > 0}
      optional={true}
    >
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, maxWidth: { sm: '75%' } }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Usage Guide:</strong>
            </Typography>
            <ul>
              <li>
                <Typography variant="body2" color="text.secondary">
                  <strong>Add users</strong> to the table below by searching the Knowledge Base.
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  <strong>Specify their role</strong> and expected specialization for this tender.
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  These team members will automatically be <strong>included as context</strong>{' '}
                  during draft generation.
                </Typography>
              </li>
            </ul>
          </Box>
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => setModalOpen(true)}
            sx={{
              backgroundColor: theme.palette.bidlogiq?.brick || 'primary.main',
              color: '#fff',
              '&:hover': {
                backgroundColor: theme.palette.bidlogiq?.brick
                  ? `${theme.palette.bidlogiq.brick}dd`
                  : 'primary.dark',
              },
            }}
          >
            Add Team Members
          </Button>
        </Box>

        <Box sx={{ mb: 2 }}>
          <StandardTable
            columns={activeColumns}
            data={activeMembers}
            keyExtractor={(member) => member.userId || member.displayName}
            emptyStateMessage="No team members added yet. Click 'Add Team Members' to find potential team members."
          />
        </Box>
      </Box>

      {/* Modal for adding team members */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Team Members</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            These potential team members were identified by matching their CVs against the tender
            requirements. Select team members to add them to your proposal.
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or specialization..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: <SearchIcon color="action" fontSize="small" sx={{ mr: 1 }} />,
              endAdornment: isSearching ? <CircularProgress color="inherit" size={20} /> : null,
            }}
          />

          <StandardTable
            columns={suggestedColumns}
            data={suggestedMembers}
            keyExtractor={(member) => member.userId || member.displayName}
            emptyStateMessage="No additional recommendations available matching your search."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </WorkflowStep>
  );
};

export default ManageTeamStep;
